import React, { useEffect, useState } from 'react';
import { FileTextIcon, VideoIcon, MicIcon, ImageIcon, ArrowDownIcon, CpuIcon, SparklesIcon, WifiOffIcon, DownloadIcon, SmartphoneIcon } from 'lucide-react';
import { Card, CardContent } from './ui/Card';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function FeaturesSection() {
  const revealRef = useScrollReveal();
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setAnimated(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.3
    });
    const bars = document.getElementById('engine-progress-bars');
    if (bars) observer.observe(bars);
    return () => observer.disconnect();
  }, []);
  const progressBars = [{
    label: 'Rozpoznanie Textu',
    value: '99%',
    width: '99%'
  }, {
    label: 'Analýza Obrazu',
    value: '97%',
    width: '97%'
  }, {
    label: 'Porozumenie Videu',
    value: '94%',
    width: '94%'
  }, {
    label: 'Spracovanie Zvuku',
    value: '92%',
    width: '92%'
  }, {
    label: 'Krížové Vyhľadávanie',
    value: '96%',
    width: '96%'
  }];
  return <section id="engine" ref={revealRef} className="py-36 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden" data-id="element-32">
      {/* Block A: Engine Intro */}
      <div className="max-w-4xl mx-auto text-center mb-28" data-id="element-33">
        <p className="reveal text-xs font-semibold tracking-[0.2em] text-apple-gray uppercase mb-8" data-id="element-34">
          CORE TECHNOLOGY
        </p>
        <h2 className="reveal reveal-delay-1 text-5xl md:text-7xl font-extrabold tracking-tight text-apple-black mb-6" data-id="element-35">
          Gemini Multimodal Embeddings 2
        </h2>
        <p className="reveal reveal-delay-2 text-2xl md:text-3xl font-light text-apple-black mb-8" data-id="element-36">
          Jeden engine. Štyri modality. Nekonečné možnosti.
        </p>
        <p className="reveal reveal-delay-3 text-lg md:text-xl text-apple-gray leading-relaxed mb-16 font-light" data-id="element-37">
          Predstavte si technológiu, ktorá rozumie svetu rovnako ako vy — vidí
          obrázky, počúva zvuky, číta texty a pozerá videá. Všetko naraz, v
          jednom okamihu. Google túto technológiu vyvinul. My z nej staviame
          reálne produkty pre reálnych ľudí.
        </p>

        {/* AI Engine Visualization Image */}
        <div className="reveal-scale reveal-delay-4 w-full rounded-[2rem] overflow-hidden shadow-xl" data-id="element-38">
          <img src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=500&fit=crop&crop=center" alt="AI neurónová sieť vizualizácia" className="w-full h-48 md:h-80 object-cover hover:scale-105 transition-transform duration-700" data-id="element-39" />
        </div>
      </div>

      {/* Block B: Visual Modality Diagram */}
      <div className="reveal relative max-w-3xl mx-auto md:h-[400px] mb-36 flex flex-col md:block items-center justify-center gap-8" data-id="element-40">
        {/* Center AI Circle */}
        <div className="relative md:absolute md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-20 w-36 h-36 bg-white rounded-full shadow-2xl flex items-center justify-center border-4 border-gray-50" data-id="element-41">
          <div className="absolute inset-0 rounded-full bg-apple-blue/20 animate-pulse" data-id="element-42"></div>
          <div className="absolute inset-2 rounded-full bg-apple-blue/10 animate-pulse" style={{
          animationDelay: '0.5s'
        }} data-id="element-43"></div>
          <span className="text-3xl font-extrabold text-apple-black relative z-10 tracking-tighter" data-id="element-44">
            AI
          </span>
        </div>

        {/* Connecting Lines (Desktop Only) */}
        <div className="hidden md:flex absolute inset-0 items-center justify-center z-0" data-id="element-45">
          <div className="w-full h-full max-w-[400px] max-h-[400px] border-2 border-dashed border-gray-200 rounded-full rotate-45" data-id="element-46"></div>
          <div className="absolute w-full h-full max-w-[400px] max-h-[400px] border-2 border-dashed border-gray-200 rounded-full -rotate-45" data-id="element-47"></div>
        </div>

        {/* 4 Modality Boxes with animated glows - Responsive Layout */}
        <div className="grid grid-cols-2 gap-4 w-full px-4 md:px-0 md:block" data-id="element-48">
          <div className="relative md:absolute md:top-0 md:left-12 w-full md:w-32 h-32 bg-white rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center justify-center z-10 overflow-hidden card-hover-premium" data-id="element-49">
            <div className="modality-glow-blue w-full h-14 absolute top-0 rounded-t-2xl" data-id="element-50"></div>
            <FileTextIcon className="w-8 h-8 text-apple-blue mb-2 relative z-10" data-id="element-51" />
            <span className="font-bold text-sm text-apple-black relative z-10" data-id="element-52">
              TEXT
            </span>
          </div>
          <div className="relative md:absolute md:top-0 md:right-12 w-full md:w-32 h-32 bg-white rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center justify-center z-10 overflow-hidden card-hover-premium" data-id="element-53">
            <div className="modality-glow-purple w-full h-14 absolute top-0 rounded-t-2xl" data-id="element-54"></div>
            <VideoIcon className="w-8 h-8 text-purple-500 mb-2 relative z-10" data-id="element-55" />
            <span className="font-bold text-sm text-apple-black relative z-10" data-id="element-56">
              VIDEO
            </span>
          </div>
          <div className="relative md:absolute md:bottom-0 md:left-12 w-full md:w-32 h-32 bg-white rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center justify-center z-10 overflow-hidden card-hover-premium" data-id="element-57">
            <div className="modality-glow-amber w-full h-14 absolute top-0 rounded-t-2xl" data-id="element-58"></div>
            <MicIcon className="w-8 h-8 text-amber-500 mb-2 relative z-10" data-id="element-59" />
            <span className="font-bold text-sm text-apple-black relative z-10" data-id="element-60">
              AUDIO
            </span>
          </div>
          <div className="relative md:absolute md:bottom-0 md:right-12 w-full md:w-32 h-32 bg-white rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center justify-center z-10 overflow-hidden card-hover-premium" data-id="element-61">
            <div className="modality-glow-emerald w-full h-14 absolute top-0 rounded-t-2xl" data-id="element-62"></div>
            <ImageIcon className="w-8 h-8 text-emerald-500 mb-2 relative z-10" data-id="element-63" />
            <span className="font-bold text-sm text-apple-black relative z-10" data-id="element-64">
              IMAGE
            </span>
          </div>
        </div>
      </div>

      {/* Block C: How It Works */}
      <div className="mb-36" data-id="element-65">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative" data-id="element-66">
          <div className="hidden md:block absolute top-8 left-[16%] right-[16%] h-0.5 border-t-2 border-dashed border-gray-200 z-0" data-id="element-67"></div>

          <div className="reveal flex flex-col items-center text-center relative z-10" data-id="element-68">
            <div className="w-16 h-16 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center mb-6" data-id="element-69">
              <span className="text-xl font-extrabold text-apple-gray" data-id="element-70">01</span>
            </div>
            <ArrowDownIcon className="w-8 h-8 text-apple-blue mb-4" data-id="element-71" />
            <h3 className="text-2xl font-bold text-apple-black mb-3" data-id="element-72">Vstup</h3>
            <p className="text-apple-gray leading-relaxed font-light" data-id="element-73">
              Pošlete text, obrázok, video alebo zvuk. Alebo všetko naraz.
            </p>
          </div>

          <div className="reveal reveal-delay-2 flex flex-col items-center text-center relative z-10" data-id="element-74">
            <div className="w-16 h-16 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center mb-6" data-id="element-75">
              <span className="text-xl font-extrabold text-apple-gray" data-id="element-76">02</span>
            </div>
            <CpuIcon className="w-8 h-8 text-apple-blue mb-4" data-id="element-77" />
            <h3 className="text-2xl font-bold text-apple-black mb-3" data-id="element-78">
              Spracovanie
            </h3>
            <p className="text-apple-gray leading-relaxed font-light" data-id="element-79">
              Engine vytvorí jednotný vektor — digitálny odtlačok významu, nie
              slov.
            </p>
          </div>

          <div className="reveal reveal-delay-4 flex flex-col items-center text-center relative z-10" data-id="element-80">
            <div className="w-16 h-16 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center mb-6" data-id="element-81">
              <span className="text-xl font-extrabold text-apple-gray" data-id="element-82">03</span>
            </div>
            <SparklesIcon className="w-8 h-8 text-apple-blue mb-4" data-id="element-83" />
            <h3 className="text-2xl font-bold text-apple-black mb-3" data-id="element-84">Výstup</h3>
            <p className="text-apple-gray leading-relaxed font-light" data-id="element-85">
              Dostanete odpoveď, ktorá rozumie kontextu naprieč všetkými
              formátmi.
            </p>
          </div>
        </div>
      </div>

      {/* Block D: Capability Progress Bars */}
      <div id="engine-progress-bars" className="reveal max-w-3xl mx-auto mb-44" data-id="element-86">
        <h3 className="text-3xl md:text-4xl font-bold text-center text-apple-black mb-14 tracking-tight" data-id="element-87">
          Výkon Enginu v Číslach
        </h3>
        <div className="space-y-8" data-id="element-88">
          {progressBars.map((bar, index) => <div key={index} className="w-full" data-id="element-89">
              <div className="flex justify-between items-end mb-3" data-id="element-90">
                <span className="text-base font-semibold text-apple-black" data-id="element-91">
                  {bar.label}
                </span>
                <span className="text-lg font-bold text-apple-blue" data-id="element-92">
                  {bar.value}
                </span>
              </div>
              <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-93">
                <div className="h-full bg-gradient-to-r from-apple-blue to-blue-400 rounded-full" style={{
              width: animated ? bar.width : '0%',
              transition: `width 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.15}s`
            }} data-id="element-94" />
              </div>
            </div>)}
        </div>
      </div>

      {/* Block E: PWA Showcase */}
      <div id="pwa" className="w-full pt-20 border-t border-gray-100" data-id="element-95">
        <div className="reveal text-center mb-20" data-id="element-96">
          <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight text-apple-black" data-id="element-97">
            Prečo PWA? Pretože budúcnosť je v prehliadači.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-98">
          <div className="reveal" data-id="element-99">
            <Card className="border-gray-200 rounded-3xl bg-white shadow-sm border overflow-hidden card-hover-premium" data-id="element-100">
              <img src="https://images.unsplash.com/photo-1526498460520-4c246339dccb?w=600&h=300&fit=crop&crop=center" alt="Offline práca" className="w-full h-40 object-cover hover:scale-105 transition-transform duration-500" data-id="element-101" />
              <CardContent className="p-10 text-center flex flex-col items-center" data-id="element-102">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6 -mt-12 relative z-10 border-4 border-white shadow-md" data-id="element-103">
                  <WifiOffIcon className="w-8 h-8 text-apple-blue" data-id="element-104" />
                </div>
                <h4 className="text-2xl font-bold text-apple-black mb-4" data-id="element-105">
                  Funguje Offline
                </h4>
                <p className="text-apple-gray leading-relaxed font-light" data-id="element-106">
                  Vaša appka beží aj bez internetu. Dáta sa synchronizujú, keď
                  sa pripojíte.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="reveal reveal-delay-2" data-id="element-107">
            <Card className="border-gray-200 rounded-3xl bg-white shadow-sm border overflow-hidden card-hover-premium" data-id="element-108">
              <img src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&h=300&fit=crop&crop=center" alt="Mobilná inštalácia" className="w-full h-40 object-cover hover:scale-105 transition-transform duration-500" data-id="element-109" />
              <CardContent className="p-10 text-center flex flex-col items-center" data-id="element-110">
                <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mb-6 -mt-12 relative z-10 border-4 border-white shadow-md" data-id="element-111">
                  <DownloadIcon className="w-8 h-8 text-purple-500" data-id="element-112" />
                </div>
                <h4 className="text-2xl font-bold text-apple-black mb-4" data-id="element-113">
                  Inštalácia Jedným Klikom
                </h4>
                <p className="text-apple-gray leading-relaxed font-light" data-id="element-114">
                  Žiadny App Store, žiadne schvaľovanie. Používateľ klikne a
                  appka je na ploche.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="reveal reveal-delay-4" data-id="element-115">
            <Card className="border-gray-200 rounded-3xl bg-white shadow-sm border overflow-hidden card-hover-premium" data-id="element-116">
              <img src="https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&h=300&fit=crop&crop=center" alt="Natívne funkcie" className="w-full h-40 object-cover hover:scale-105 transition-transform duration-500" data-id="element-117" />
              <CardContent className="p-10 text-center flex flex-col items-center" data-id="element-118">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6 -mt-12 relative z-10 border-4 border-white shadow-md" data-id="element-119">
                  <SmartphoneIcon className="w-8 h-8 text-emerald-500" data-id="element-120" />
                </div>
                <h4 className="text-2xl font-bold text-apple-black mb-4" data-id="element-121">
                  Natívny Výkon
                </h4>
                <p className="text-apple-gray leading-relaxed font-light" data-id="element-122">
                  Push notifikácie, prístup ku kamere, GPS, NFC — všetko čo
                  poznáte z natívnych appiek.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}